// KanbanCard.tsx
import { Box, Typography } from "@mui/material";
import { useDraggable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { CSS } from "@dnd-kit/utilities";
import { useRef, useState } from "react";

import "./KanbanCard.scss";

export default function KanbanCard({
	title,
	index,
	parent,
	ideaDate,
	authors,
	idea_id,
	onCardClick,
	isDragging = false,
	area,
}: {
	title: string;
	ideaDate: string;
	index: number;
	parent: string;
	authors: any[];
	idea_id: number;
	onCardClick?: (idea_id: number) => void;
	isDragging?: boolean;
	area: string;
}) {
	const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
	const threshold = 5;

	const handleMouseDown = (e: React.MouseEvent) => {
		mouseDownPos.current = { x: e.clientX, y: e.clientY };
	};

	const handleMouseUp = (e: React.MouseEvent) => {
		if (!mouseDownPos.current) return;

		const dx = Math.abs(e.clientX - mouseDownPos.current.x);
		const dy = Math.abs(e.clientY - mouseDownPos.current.y);

		if (dx < threshold && dy < threshold && onCardClick) {
			onCardClick(idea_id);
		}

		mouseDownPos.current = null;
	};

	const { attributes, listeners, setNodeRef, transform } = useDraggable({
		id: title,
		data: {
			title,
			index,
			parent,
			created_at: ideaDate,
			authors,
			idea_id,
		},
	});
	const style = {
		transform: CSS.Translate.toString(transform),
	};
	const statusColor = () => {
		switch (parent) {
			case "IDEA":
				return "#0CB3EB";
			case "DESARROLLO":
				return "#EBAC00";
			case "EN REVISIÓN":
				return "#97D700";
			case "FINALIZADO":
				return "#671E75";
			case "IMPLEMENTACIÓN":
				return "#066964";
			case "BACKLOG":
				return "#BEBEBE";
		}
	};

	const hoverBgColor = () => {
		switch (parent) {
			case "IDEA":
				return "#DFEDFA";
			case "DESARROLLO":
				return "#FFFCE7";
			case "EN REVISIÓN":
				return "#FBFFE4";
			case "FINALIZADO":
				return "#F8E9FE";
			case "IMPLEMENTACIÓN":
				return "#C5FFF6";
			case "BACKLOG":
				return "#F8F8F9";
		}
	};

	const hoverBorderColor = () => {
		switch (parent) {
			case "IDEA":
				return "#4E92E3";
			case "EN REVISIÓN":
				return "#97D700";
			case "DESARROLLO":
				return "#EBAC00";
			case "IMPLEMENTACIÓN":
				return "#066964";
			case "FINALIZADO":
				return "#671E75";
			case "BACKLOG":
				return "#BEBEBE";
		}
	};

	return (
		<Box
			className="kanban-card"
			sx={{
				transform: style.transform,
				borderRightColor: `${statusColor()} !important`,
				opacity: isDragging ? 0 : 1,
				":hover": {
					// outlineColor: statusColor(),
					backgroundColor: hoverBgColor(),
					outline: `2.5px solid ${hoverBorderColor()}`,
				},
			}}
			onMouseDown={handleMouseDown}
			onMouseUp={handleMouseUp}
			{...listeners}
			{...attributes}
			ref={setNodeRef}
		>
			<Box
				className="kanban-card__text"
				sx={{ justifyContent: "start", display: "flex" }}
			>
				<GripVertical
					className="kanban-card__text__icon"
					style={{ "--status-color": statusColor() } as React.CSSProperties}
				></GripVertical>
				<Box className="kanban-card__text__label">
					<Typography>{title}</Typography>
					<Typography sx={{ fontSize: "18px", color: "#A3A3A3" }}>
						{area}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
}
